import { useState, useMemo } from "react";
import { Box, Grid2, Modal, Typography } from "@mui/material";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import SelectBoxField from "../formfields/SelectBoxField";
import dayjs from "dayjs";
import { useApartmentUnderTrainer, useTrainerSessions } from "../../../assets/api/useTrainerGetApi";
import useAuthStore from "../../../store/useAuthStore";

const ScheduleCalendar = () => {
  const { user } = useAuthStore();
  const [selectedApartment, setSelectedApartment] = useState("");
  const { data: apartmentList, isLoading: isApartmentList } = useApartmentUnderTrainer({ trainer: user?.id });
  const apartmentOption = apartmentList?.map((item) => ({
    id: item?.apartment_name,
    label: item?.apartment_name,
    value: item?.apartment_name,
  }))
  const { data: trainerSessions, isLoading: isSessionsLoading } = useTrainerSessions({ trainer_id: user?.id, apartment_name: selectedApartment })
  const schedule = trainerSessions?.schedule
  const currentYear = new Date().getFullYear();
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [date, setDate] = useState(new Date());
  const [modalData, setModalData] = useState(null);
  const months = [...Array(12)].map((_, i) => ({
    id: i,
    value: i,
    label: new Date(0, i).toLocaleString("default", { month: "long" })
  }));
  const years = Array.from({ length: 50 }, (_, i) => ({
    id: currentYear - 20 + i,
    value: currentYear - 20 + i,
    label: `${currentYear - 20 + i}`
  }));
  const handleApartmentChange = (value) => {
    setSelectedApartment(value)
  };
  const handleMonthChange = (value) => {
    setSelectedMonth(value);
    setDate(new Date(selectedYear, value, 1));
  };
  const handleYearChange = (value) => {
    setSelectedYear(value);
    setDate(new Date(value, selectedMonth, 1));
  };
  const getMatchingSchedulesForDate = (d) => {
    const weekday = d.toLocaleDateString("en-US", { weekday: "long" });
    const sessions = schedule?.[weekday] || [];
    return sessions.filter((s) => {
      return (
        (!selectedApartment || s.apartment === selectedApartment) &&
        dayjs(d).isAfter(dayjs(s.batch_start_date).subtract(1, "day")) &&
        dayjs(d).isBefore(dayjs(s.batch_end_date).add(1, "day"))
      );
    });
  };

  const eventDatesMap = useMemo(() => {
    const map = {};
    if (!schedule) return map;
    Object.entries(schedule)?.forEach(([day, items]) => {
      items.forEach((entry) => {
        if (!selectedApartment || entry.apartment === selectedApartment) {
          const start = dayjs(entry.batch_start_date);
          const end = dayjs(entry.batch_end_date);
          for (let d = start; d.isBefore(end) || d.isSame(end); d = d.add(1, "day")) {
            if (d.format("dddd") === day) {
              const key = d.format("YYYY-MM-DD");
              if (!map[key]) map[key] = [];
              map[key].push(entry);
            }
          }
        }
      });
    });
    return map;
  }, [selectedApartment, schedule]);

  return (
    <Grid2 container spacing={2}>
      <Grid2 children size={{ xs: 12, sm: 5 }}>
        <Typography variant="h6" color="gray" mb={2}>
          Upcoming schedules
        </Typography>

        <SelectBoxField
          label="Month"
          options={months}
          onChange={handleMonthChange}
          value={months.find((m) => m.value === selectedMonth)}
          isShowCross={false}
        />

        <SelectBoxField
          label="Year"
          options={years}
          onChange={handleYearChange}
          value={years.find((y) => y.value === selectedYear)}
          sx={{ my: 1 }}
          isShowCross={false}
        />

        <SelectBoxField
          label="Apartment"
          options={apartmentOption}
          onChange={handleApartmentChange}
          value={selectedApartment}
          isloading={isApartmentList}

        />
      </Grid2>

      <Grid2 children size={{ xs: 12, sm: 7 }}>
        <Calendar
          onChange={(val) => {
            setDate(val);
            const sessions = getMatchingSchedulesForDate(val);
            if (sessions.length) {
              setModalData({ date: val, sessions });
            }
          }}
          value={date}
          activeStartDate={new Date(selectedYear, selectedMonth, 1)}
          tileContent={({ date }) => {
            const key = dayjs(date).format("YYYY-MM-DD");
            return eventDatesMap[key] ? <div className="dot" /> : null;
          }}
        />
      </Grid2>

      <Modal open={!!modalData} onClose={() => setModalData(null)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper', boxShadow: 24,
          p: 3, borderRadius: 2, minWidth: 300
        }}>
          <Typography variant="h6" mb={1}>
            {dayjs(modalData?.date).format("dddd, MMMM D")}
          </Typography>
          {modalData?.sessions.map((s, i) => (
            <Box key={i} sx={{ mb: 1 }}>
              <Typography variant="subtitle1">{s.course}</Typography>
              <Typography variant="body2">Apartment: {s.apartment}</Typography>
              <Typography variant="body2">Time: {s.session_start_time} - {s.session_end_time}</Typography>
            </Box>
          ))}
        </Box>
      </Modal>
    </Grid2>
  );
};

export default ScheduleCalendar;
